/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import useGlobalProps from '@ion/dls/src/props/useGlobalProps';
import {SearchFilters} from '@ion/features/src/search/types';
import {createSortParams} from '../../helpers';
import useCurrentGlobalGroupId from '../../hooks/useCurrentGlobalGroupId';
import {useAuthenticatedUser} from '../../pages/admin/users/useAuthenticatedUser';
import {createSortAndSearchQueryParameters} from './ModularSearchUtils';
import {devicesModularSearchSelector} from './selector';

/**
 * The useDevicesGridSearch hook is used to retrieve any field search query.
 * 
 * ```typescript
 * 
 *  import {useDevicesGridSearch} from '@ion/packages/query/src/hooks';
 * 
 *  const {search} = useDevicesGridSearch();
 * 
 * ```
 */
export const useDevicesGridSearch = (
  sortOn: string[],
  columnKeys?: string[],
  isVbuFwEnabled: boolean = true
): SearchFilters => {
  const search                    = useSelector(devicesModularSearchSelector);
  const loggedinAccountId         = entityId(useAuthenticatedUser()?.account);
  const globalGroupId             = useCurrentGlobalGroupId();
  const [anySearch, setAnySearch] = useState<SearchFilters>();

  const anySearchForOnlyDisplayedColumns = useGlobalProps('DevicesGrid.anySearchForOnlyDisplayedColumns') as boolean;

  useEffect(() => {
    setAnySearch(createSortParams({
      item        : sortOn?.[0] || 'name',
      id          : globalGroupId || loggedinAccountId,
      queryParams : createSortAndSearchQueryParameters(search, isVbuFwEnabled),
      columnKeys  : anySearchForOnlyDisplayedColumns ? columnKeys : null,
      isVbuFwEnabled
    }));
  }, [sortOn, columnKeys]);

  return anySearch;
};

export default useDevicesGridSearch;
